import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Divider,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import useTabTitle from "../hooks/useTabTitle.hook";
import AppContainer from "../components/AppContainer";
import {
  inputStyle,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { useNavigate } from "react-router-dom";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  getAppVersion,
  updateAppVersion,
} from "../redux/slices/mobileAppAlertsSlice";
import LoadingOverlay from "../components/LoadingOverlay";

type VersionData = {
  versionCode: number | string;
  versionName: string;
  notes: string;
  releaseDate: string | Date;
};

const VersionManager: React.FC = () => {
  useTabTitle();
  useDetectSession();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { appVersions, status, error, message } = useSelector(
    (state: RootState) => state.mobileAppAlerts
  );
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const idToken = localStorage.getItem("idToken");

  const appPackageNames = [
    {
      packageName: "com.malayan.MalayanMobile.TEST",
      label: "Test Environment",
    },
    {
      packageName: "com.malayan.MalayanMobile",
      label: "Production Environment",
    },
  ];
  const [appPackageName, setAppPackageName] = useState<
    "com.malayan.MalayanMobile" | "com.malayan.MalayanMobile.TEST"
  >("com.malayan.MalayanMobile.TEST");

  const [androidEditMode, setAndroidEditMode] = useState<boolean>(false);
  const [iosEditMode, setIosEditMode] = useState<boolean>(false);

  const [androidData, setAndroidData] = useState<VersionData>({
    versionCode: "1.0.0",
    versionName: "Android Release",
    notes: "Initial release for Android.",
    releaseDate: "2025-07-23",
  });

  const [iosData, setIosData] = useState<VersionData>({
    versionCode: "1.0.0",
    versionName: "iOS Release",
    notes: "Initial release for iOS.",
    releaseDate: "2025-07-23",
  });

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const handleChange =
    (platform: "android" | "ios", field: keyof VersionData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (platform === "android") {
        setAndroidData({ ...androidData, [field]: value });
      } else {
        setIosData({ ...iosData, [field]: value });
      }
    };

  const handleEdit = (platform: "android" | "ios") => {
    if (platform === "android" && !androidEditMode) {
      setAndroidEditMode(!androidEditMode);
      return;
    } else if (platform === "ios" && !iosEditMode) {
      setIosEditMode(!iosEditMode);
      return;
    }

    if (platform === "android") {
      dispatch(
        updateAppVersion({
          ...androidData,
          appId: appPackageName,
          platform: "android",
          updatedByUser: currentUser?.fullname ?? "",
        })
      ).then(() => {
        setAndroidEditMode(!androidEditMode);
      });
    } else {
      dispatch(
        updateAppVersion({
          ...iosData,
          appId: appPackageName,
          platform: "ios",
          updatedByUser: currentUser?.fullname ?? "",
        })
      ).then(() => {
        setIosEditMode(!iosEditMode);
      });
    }
  };

  const renderSection = (
    title: string,
    data: VersionData,
    isEditMode: boolean,
    setIsEditMode: (val: boolean) => void,
    onChange: (
      field: keyof VersionData
    ) => (e: React.ChangeEvent<HTMLInputElement>) => void,
    onToggleEdit: () => void
  ) => (
    <Box mb={6}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Version Code"
            fullWidth
            value={data.versionCode}
            onChange={onChange("versionCode")}
            InputProps={{ readOnly: !isEditMode }}
            disabled={!isEditMode}
            sx={inputStyle()}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Version Name"
            fullWidth
            value={data.versionName}
            onChange={onChange("versionName")}
            InputProps={{ readOnly: !isEditMode }}
            disabled={!isEditMode}
            sx={inputStyle()}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={1}
            value={data.notes}
            onChange={onChange("notes")}
            InputProps={{ readOnly: !isEditMode }}
            disabled={!isEditMode}
            sx={inputStyle()}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Release Date"
            fullWidth
            type="datetime-local"
            value={data.releaseDate}
            onChange={onChange("releaseDate")}
            InputProps={{ readOnly: !isEditMode }}
            InputLabelProps={{ shrink: true }}
            disabled={!isEditMode}
            sx={inputStyle()}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            {isEditMode && (
              <Button
                variant="outlined"
                onClick={() => setIsEditMode(!isEditMode)}
                sx={primaryBtnStyleOutline}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 15 }}
              sx={primaryBtnStyle}
              onClick={onToggleEdit}
            >
              {isEditMode ? "Save" : "Edit"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderOptionsSection = () => {
    return (
      <Grid item xs={12} md={3}>
        <Paper elevation={3} sx={{ padding: 2 }}>
          <Typography variant="h4" gutterBottom>
            App Version Manager
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Select App Environment
          </Typography>

          {Object.values(appPackageNames).map(
            (pkg: { packageName: any; label: string }) => (
              <Box
                key={pkg.packageName}
                onClick={() => setAppPackageName(pkg.packageName)}
                sx={{
                  padding: 2,
                  marginBottom: 1,
                  borderRadius: 30,
                  cursor: "pointer",
                  backgroundColor:
                    appPackageName === pkg.packageName ? "#eef4fc" : "grey.100",
                  color: "text.primary",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor:
                      appPackageName === pkg.packageName
                        ? "#eef4fc"
                        : "grey.200",
                  },
                }}
              >
                {pkg.label}
              </Box>
            )
          )}
        </Paper>
      </Grid>
    );
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }
  }, [dispatch, idToken, navigate]);

  useEffect(() => {
    dispatch(getAppVersion("com.malayan.MalayanMobile.TEST"));
    dispatch(getAppVersion("com.malayan.MalayanMobile"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAndroidData(appVersions[appPackageName].versionData.android);
    setIosData(appVersions[appPackageName].versionData.ios);
  }, [appVersions, appPackageName]);

  useEffect(() => {
    if (status === "succeeded" && message) {
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } else if (status === "failed" && message) {
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  }, [status, message]);

  if (error) {
    return (
      <AppContainer>
        <Grid container sx={{ height: "100vh", overflow: "hidden" }}>
          <Grid container spacing={4} sx={{ padding: 4 }} position={"absolute"}>
            {renderOptionsSection()}
            <Grid
              item
              xs={12}
              md={9}
              sx={{
                height: "90vh",
                overflowY: "auto",
                padding: 2,
              }}
            >
              <Typography variant="h6" color="error">
                Error: {error}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Grid container sx={{ height: "110vh", overflow: "hidden" }}>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ top: "150px" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
          >
            {message}
          </Alert>
        </Snackbar>
        <Grid container spacing={4} sx={{ padding: 4 }} position={"absolute"}>
          {renderOptionsSection()}

          <Grid
            item
            xs={12}
            md={9}
            sx={{
              height: "100vh",
              overflowY: "auto",
              padding: 2,
            }}
          >
            {status === "succeeded" ? (
              <Paper
                elevation={4}
                sx={{
                  padding: 4,
                  maxWidth: 850,
                }}
              >
                <Typography variant="h5" gutterBottom>
                  {
                    appPackageNames.find(
                      (item) => item.packageName === appPackageName
                    )?.label
                  }{" "}
                  - {appPackageName}
                </Typography>

                {renderSection(
                  "Android Version",
                  androidData,
                  androidEditMode,
                  setAndroidEditMode,
                  (field) => handleChange("android", field),
                  () => handleEdit("android")
                )}

                {renderSection(
                  "iOS Version",
                  iosData,
                  iosEditMode,
                  setIosEditMode,
                  (field) => handleChange("ios", field),
                  () => handleEdit("ios")
                )}
              </Paper>
            ) : (
              <LoadingOverlay loading />
            )}
          </Grid>
        </Grid>
      </Grid>
    </AppContainer>
  );
};

export default VersionManager;
